import React from 'react'

import { Avatar, Grid, Paper, Typography, Divider } from '@mui/material'
import { Email as EmailIcon, SpeakerNotes as SpeakerNotesIcon, Phone as PhoneIcon } from '@mui/icons-material'

import { formatAgentLetterAvatar } from '../../utils/formatAgentLetterAvatar'
import { formatName, formatPhoneNumber } from '@edge/spark/utils'
import { ContactModal, type Agent, type Agents } from '../../../feature-contact-modal'
import { AALogoColor } from '@edge/spark/ui-assets'
import { MUIRouterLink } from '@edge/spark/ui-mui'

interface AgentProps {
    agentId?: number
    agentName?: string
}

interface Props {
    accountId: number
    agent: Agent
    agents: Agents
    companyData?: any
}

export const CompanyAgentCard: React.FC<Props> = ({ agent, accountId, agents, companyData }: Props) => {
    const {
        cellPhone,
        email,
        agentId,
        auctionAccessNumber,
        auctionAccessPicture,
        // agentNotes,
    } = agent

    const agentProps: AgentProps = {
        agentId: agent.agentId,
        agentName: formatName(agent),
    }

    const [modalRow, setModalRow] = React.useState(agentProps)
    const [open, setOpen] = React.useState(false)
    const onOpen: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
        e.preventDefault()
        setModalRow(agentProps)
        setOpen(true)
    }
    const onClose = (): void => {
        setModalRow({ agentId: undefined, agentName: undefined })
        setOpen(false)
    }

    const [validSrc, setValidSrc] = React.useState<string | null | undefined>(auctionAccessPicture || null || undefined)

    return (
        <div>
            {open && (
                <ContactModal
                    onClose={onClose}
                    open={open}
                    accountId={accountId}
                    agents={agents}
                    row={modalRow}
                    fromAgentCard
                />
            )}

            <Paper elevation={2} sx={{ p: 1, width: '100%', minHeight: 107 }}>
                <MUIRouterLink
                    sx={{ textDecoration: 'none' }}
                    href={`/details/agents?id=${agentId}&accountKey=${companyData?.accountKey}${
                        companyData?.auctionAccessNumber ? '&caan=' + companyData?.auctionAccessNumber : ''
                    }`}
                >
                    <Grid container direction="column">
                        {auctionAccessNumber && (
                            <Grid item container justifyContent="flex-end">
                                <Typography
                                    variant="caption"
                                    sx={{ display: 'flex', alignItems: 'right', fontStyle: 'italic' }}
                                    gutterBottom
                                >
                                    <img
                                        style={{ marginRight: '18px' }}
                                        height="18px"
                                        alt="Auction Access:"
                                        src={AALogoColor}
                                    />
                                    {`${auctionAccessNumber}`}
                                </Typography>
                            </Grid>
                        )}
                        <Grid
                            item
                            container
                            direction="row"
                            spacing={2}
                            sx={{ display: 'flex', padding: '0 0 16px 0' }}
                        >
                            <Grid item>
                                {validSrc ? (
                                    <Avatar
                                        imgProps={{
                                            onError: (): void => {
                                                setValidSrc(undefined)
                                            },
                                        }}
                                        src={validSrc}
                                        sx={{ width: '56px', height: '56px' }}
                                    />
                                ) : (
                                    <Avatar sx={{ width: '56px', height: '56px' }}>
                                        {formatAgentLetterAvatar(agent)}
                                    </Avatar>
                                )}
                            </Grid>
                            <Grid item>
                                <Grid container direction="column">
                                    <Grid item>
                                        <Typography sx={{ fontWeight: 500, color: 'rgba(0,0,0,0.87)' }} variant="body1">
                                            {formatName(agent)}
                                        </Typography>
                                    </Grid>
                                    {cellPhone && (
                                        <Grid item>
                                            <Typography sx={{ fontSize: '12px' }}>
                                                {formatPhoneNumber(cellPhone)}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {email && (
                                        <Grid item>
                                            <Typography sx={{ fontSize: '12px' }}>
                                                {`${email.toLowerCase()}`}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </MUIRouterLink>
                <Divider sx={{ margin: '0 -5px 0 -5px' }} />
                <Grid container sx={{ display: 'flex', padding: '16px 0 0 0' }}>
                    <Grid item container direction="row" xs={6} justifyContent="flex-start" alignItems="center">
                        <MUIRouterLink href="#" onClick={onOpen}>
                            <SpeakerNotesIcon sx={{ mr: 1 }} color="primary" fontSize="small" />
                        </MUIRouterLink>
                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            <MUIRouterLink href="#" onClick={onOpen} color="primary" underline="hover">
                                Add Contact Log
                            </MUIRouterLink>
                        </Typography>
                    </Grid>
                    <Grid item container direction="row" xs={6} justifyContent="flex-end">
                        {cellPhone && (
                            <MUIRouterLink href={`tel:${cellPhone}`} target="_blank">
                                <PhoneIcon sx={{ ml: 1.5 }} color="primary" fontSize="small" />
                            </MUIRouterLink>
                        )}
                        {email && (
                            <MUIRouterLink href={`mailto:${email.toLowerCase()}`} target="_blank">
                                <EmailIcon sx={{ ml: 1.5 }} color="primary" fontSize="small" />
                            </MUIRouterLink>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </div>
    )
}
